/**
 * @typedef {import('./arcade/arcade.types').PageOptions} PageOptions
 * @typedef {import('../baseView').BaseViewOptions} BaseViewOptions
 * @typedef {import('../genericButton').GenericButtonOptions} GenericButtonOptions
 * @typedef {import('../engine/leaderboards/leaderboards.types').LeaderboardConfig} LeaderboardConfig
 *
 *
 * @typedef {object} BaseGamePartial
 * @property {string} [gameId]
 * @property {string} [contentUrl]
 * @property {boolean} [fetchProgress]
 * @property {SCORE_TYPES} [scoreType]
 * @property {string[]|undefined} [content]
 * @property {number} [contentLimit]
 * @property {boolean} [shuffle]
 * @property {boolean} [repeatQuestions]
 * @property {number} [roundIndex]
 * @property {InstructionsOptions} [instructions]
 * @property {number} [countDown]
 * @property {string[]} [countValues]
 * @property {string} [title]
 * @property {string} [subtitle]
 * @property {Record<HEADER_LABELS, string>} [labels] Labels to display on the view
 * @property {number} [score]
 * @property {number} [pointsPerQuestion]
 * @property {number} [timeLimit]
 * @property {BAR_TIMER_ORIENTATIONS} [timerOrientation]
 * @property {number} [tickInterval]
 * @property {number} [warningTime]
 * @property {PageOptions[]} [pages]
 * @property {EndscreenOptions} [endscreen]
 * @property {LeaderboardConfig} [leaderboard]
 *
 *
 * @typedef {object} InstructionsOptions
 * @property {string} image
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {GenericButtonOptions} btn
 *
 *
 * @typedef {object} BaseRoundOptions
 * @property {string} id
 * @property {string|number} [selectedOptionId]
 * @property {number} [timesAnswered]
 * @property {number} [score]
 * @property {string[]} [rewards]
 *
 *
 * @typedef {object} FlourishOptions
 * @property {string} [id]
 * @property {number} [duration]
 * @property {string} [message]
 *
 *
 * @typedef {object} EndscreenCondition
 * @property {ENDSCREEN_COMPARATOR} comparator
 * @property {ENDSCREEN_OPERANDS} propertyName
 * @property {number|string} targetValue
 *
 *
 * @typedef {object} EndscreenOptions
 * @property {string} id
 * @property {EndscreenCondition} condition
 * @property {string} title
 * @property {string} subtitle
 * @property {string} imgUrl
 * @property {string} scoreTitle
 * @property {string} scorePrefix
 * @property {string} scoreDivider
 * @property {string} scorePostfix
 * @property {string[]} messages
 * @property {string[]} [rewards]
 *
 *
 * @typedef {BaseViewOptions & BaseGameOptions} BaseGameViewOptions
 * @typedef {PageOptions & BaseGamePartial} BaseGameOptions
 */

/**
 * @enum {typeof ATTRIBUTES[keyof typeof ATTRIBUTES]}
 */
export const ATTRIBUTES = Object.freeze({
	type: 'type',
	gameId: 'gameId',
	name: 'name',
	title: 'title',
	subtitle: 'subtitle',
	instructions: 'instructions',
	scoreType: 'scoreType',
	roundIndex: 'roundIndex',
	contentUrl: 'contentUrl',
	content: 'content',
	contentLimit: 'contentLimit',
	shuffle: 'shuffle',
	repeatQuestions: 'repeatQuestions',
	timeLimit: 'timeLimit',
	tickInterval: 'tickInterval',
	timerOrientation: 'timerOrientation',
	warningTime: 'warningTime',
	fetchProgress: 'fetchProgress',
	countDown: 'countDown',
	countValues: 'countValues',
	pointsPerQuestion: 'pointsPerQuestion',
	score: 'score',
	labels: 'labels',
	endscreen: 'endscreen',
	leaderboard: 'leaderboard',

	// Runtime only properties
	pages: 'pages',
});

/**
 * @enum {typeof EVENTS[keyof typeof EVENTS]}
 */
export const EVENTS = Object.freeze({
	initialized: 'initialized',
	gameRound: 'game-round',
	gameComplete: 'game-complete',
});

/**
 * @enum {typeof HEADER_LABELS[keyof typeof HEADER_LABELS]}
 */
export const HEADER_LABELS = Object.freeze({
	timer: 'timer',
	progress: 'progress',
});

/**
 * @enum {typeof SCORE_TYPES[keyof typeof SCORE_TYPES]}
 */
export const SCORE_TYPES = Object.freeze({
	points: 'points',
	timed: 'timed',
	timedPoints: 'timedPoints',
});

/**
 * @enum {typeof ENDSCREEN_COMPARATOR[keyof typeof ENDSCREEN_COMPARATOR]}
 */
export const ENDSCREEN_COMPARATOR = Object.freeze({
	greaterThan: 'greaterThan',
	greaterThanEqualTo: 'greaterThanEqualTo',
	lessThan: 'lessThan',
	lessThanEqualTo: 'lessThanEqualTo',
	equalTo: 'equalTo',
});

/**
 * @enum {typeof ENDSCREEN_OPERANDS[keyof typeof ENDSCREEN_OPERANDS]}
 */
export const ENDSCREEN_OPERANDS = Object.freeze({
	score: 'score',
	percentage: 'percentage',
});

/**
 * @enum {typeof BAR_TIMER_ORIENTATIONS[keyof typeof BAR_TIMER_ORIENTATIONS]}
 */
export const BAR_TIMER_ORIENTATIONS = Object.freeze({
	horizontal: 'horizontal',
	vertical: 'vertical',
});

/**
 * @enum {typeof ROUND_ATTRIBUTES[keyof typeof ROUND_ATTRIBUTES]}
 */
export const ROUND_ATTRIBUTES = Object.freeze({
	id: 'id',
	selectedOptionId: 'selectedOptionId',
	rewards: 'rewards',

	/** RUNTIME ATTRIBUTES */
	timesAnswered: 'timesAnswered',
	score: 'score',
});

/**
 * @enum {typeof ENDSCREEN_ATTRIBUTES[keyof typeof ENDSCREEN_ATTRIBUTES]}
 */
export const ENDSCREEN_ATTRIBUTES = Object.freeze({
	id: 'id',
	condition: 'condition',
	title: 'title',
	subtitle: 'subtitle',
	imgUrl: 'imgUrl',
	scoreTitle: 'scoreTitle',
	scorePrefix: 'scorePrefix',
	scoreDivider: 'scoreDivider',
	scorePostfix: 'scorePostfix',
	messages: 'messages',
	rewards: 'rewards',
});
